// Modal "Adicionar empresa" da tela /companies.
// Mockup: "Modal Nova empresa (CompaniesModal · cria admin inicial)".
//
// Os campos "Nº de atendentes" e "Nº de conexões" que aparecem no mockup NÃO
// entram: `numberAttendants` / `numberConections` não são lidos por
// CreateCompanyService — quem define esses limites é o plano. Eram campos
// mortos, já comentados no código antigo.
import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles, alpha } from "@material-ui/core/styles";

import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  firstRow: { display: "flex", gap: 12, alignItems: "flex-end" },
  activeBox: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    height: 38,
    boxSizing: "border-box",
    padding: "0 8px 0 12px",
    borderRadius: 10,
    fontSize: 12.5,
    fontWeight: 600,
    flex: "none",
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(AEROS.defaultPrimary, 0.35)}`,
    backgroundColor: alpha(AEROS.defaultPrimary, 0.06),
  },
  submitWrapper: { position: "relative" },
  progress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const CompanySchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Nome é obrigatório"),
  email: Yup.string().email("Email é inválido").required("E-mail é obrigatório"),
});

const CompanyModal = ({ open, onClose, companyId }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();

  const initialState = {
    name: "",
    email: "",
    passwordDefault: "",
    status: true,
  };

  const [company, setCompany] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      if (!companyId) return;
      try {
        const { data } = await api.get(`/companies/listPlan/${companyId}`);
        setCompany((prevState) => ({ ...prevState, ...data }));
      } catch (err) {
        toastError(err);
      }
    };

    fetchCompany();
  }, [companyId, open]);

  const handleClose = () => {
    onClose();
    setCompany(initialState);
    setShowPassword(false);
  };

  const handleSaveCompany = async (values) => {
    const companyData = { ...values };
    try {
      if (companyId) {
        await api.put(`/companies/${companyId}`, companyData);
      } else {
        await api.post("/companies", companyData);
      }
      toast.success(i18n.t("companyModal.success"));
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <Formik
      initialValues={company}
      enableReinitialize={true}
      validationSchema={CompanySchema}
      onSubmit={(values, actions) => {
        setTimeout(() => {
          handleSaveCompany(values);
          actions.setSubmitting(false);
        }, 400);
      }}
    >
      {({ values, touched, errors, isSubmitting, setFieldValue, submitForm }) => (
        <AerosDialog
          open={open}
          onClose={handleClose}
          width={560}
          title={
            companyId
              ? i18n.t("companyModal.title.edit")
              : i18n.t("companyModal.title.add")
          }
          subtitle={i18n.t("aeros.companies.newModal.subtitle")}
          footer={
            <div className={m.footer}>
              <div className={m.footerActions}>
                <Button
                  className={m.cancelButton}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {i18n.t("companyModal.buttons.cancel")}
                </Button>
                <div className={classes.submitWrapper}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={m.submitButton}
                    onClick={submitForm}
                    disabled={isSubmitting}
                  >
                    {companyId
                      ? i18n.t("companyModal.buttons.okEdit")
                      : i18n.t("companyModal.buttons.okAdd")}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.progress} />
                  )}
                </div>
              </div>
            </div>
          }
        >
          <Form>
            <div className={classes.firstRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label className={m.fieldLabel} htmlFor="company-name">
                  {i18n.t("companyModal.form.name")} *
                </label>
                <Field
                  as={TextField}
                  id="company-name"
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  fullWidth
                  className={m.input}
                />
              </div>
              <div className={classes.activeBox}>
                {i18n.t("aeros.companies.newModal.active")}
                <Switch
                  color="primary"
                  size="small"
                  checked={Boolean(values.status)}
                  onChange={(e) => setFieldValue("status", e.target.checked)}
                  inputProps={{
                    "aria-label": i18n.t("aeros.companies.newModal.active"),
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label className={m.fieldLabel} htmlFor="company-email">
                {i18n.t("aeros.companies.modal.adminEmail")} *
              </label>
              <Field
                as={TextField}
                id="company-email"
                name="email"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                variant="outlined"
                fullWidth
                className={m.input}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <label className={m.fieldLabel} htmlFor="company-password">
                {i18n.t("companyModal.form.passwordDefault")} *
              </label>
              <Field
                as={TextField}
                id="company-password"
                name="passwordDefault"
                variant="outlined"
                fullWidth
                className={m.input}
                error={touched.passwordDefault && Boolean(errors.passwordDefault)}
                helperText={touched.passwordDefault && errors.passwordDefault}
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={i18n.t(
                          showPassword
                            ? "aeros.companies.modal.hidePassword"
                            : "aeros.companies.modal.showPassword"
                        )}
                        onClick={() => setShowPassword((e) => !e)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={m.hint} style={{ marginTop: 14 }}>
              <InfoOutlinedIcon />
              {i18n.t("aeros.companies.newModal.note")}
            </div>
          </Form>
        </AerosDialog>
      )}
    </Formik>
  );
};

export default CompanyModal;
